import type { DocumentOrMessage, DocumentsAndMessagesResponse, WorkbenchDocument, WorkbenchMessage } from "./api/types";
import { contentTypeFromFileName } from "./contentType";

/** BFF document status that marks a document as openable in the viewer. */
const VIEWABLE_STATUS = "VIEWABLE";

export interface DocumentsAndMessagesViewModel {
  /** Documents + messages unified and sorted newest → oldest. */
  items: DocumentOrMessage[];
  unreadMessageCount: number;
}

/**
 * Maps a document into the unified list item. Prefers the human `fileName`,
 * falling back to `docType` when the source doesn't supply one; `uploadedByOraid`
 * becomes the row author when present. Documents are never "unread" — only
 * messages carry a read/unread signal.
 * @param {WorkbenchDocument} document - A single document from the response.
 * @returns {DocumentOrMessage} The normalized list item.
 */
function mapDocument(document: WorkbenchDocument): DocumentOrMessage {
  const fileName = document.fileName ?? document.docType;
  return {
    id: document.docId,
    kind: "doc",
    name: fileName,
    receivedAt: document.uploadedAt,
    author: document.uploadedByOraid ?? undefined,
    unread: false,
    docId: document.docId,
    fileName,
    // Derive the type from the same resolved name shown as the viewer title, so
    // the row's openability and the viewer's renderer stay consistent.
    contentType: contentTypeFromFileName(fileName),
    viewable: document.status === VIEWABLE_STATUS,
    reviewedBy: document.reviewedBy,
  };
}

/**
 * Maps a message into the unified list item. The `messages` leg is stubbed today,
 * so every field is read defensively. Only an explicit `read: false` marks the
 * row unread; a missing `read` field is treated as not-unread.
 * @param {WorkbenchMessage} message - A single message from the response.
 * @param {number} index - Position, used to synthesize a stable id when the BFF omits one.
 * @returns {DocumentOrMessage} The normalized list item.
 */
function mapMessage(message: WorkbenchMessage, index: number): DocumentOrMessage {
  return {
    id: message.messageId ?? `message-${index}`,
    kind: "message",
    name: message.subject ?? message.body ?? "Message",
    receivedAt: message.receivedAt ?? "",
    author: message.author ?? undefined,
    unread: message.read === false,
  };
}

/**
 * Flattens the docs-messages response into a single newest-first list plus the
 * unread badge count. Sorting relies on the fixed-width `"YYYY-MM-DD HH:mm:ss"`
 * timestamp format, where lexical order equals chronological order.
 * @param {DocumentsAndMessagesResponse} response - The raw docs-messages payload.
 * @returns {DocumentsAndMessagesViewModel} The list + unread count for the panel.
 */
export function mapDocumentsAndMessages(response: DocumentsAndMessagesResponse): DocumentsAndMessagesViewModel {
  const { data } = response;
  const documents = data.documents ?? [];
  const messages = data.messages ?? [];

  const items: DocumentOrMessage[] = [
    ...documents.map((document) => mapDocument(document)),
    ...messages.map((message, index) => mapMessage(message, index)),
  ].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));

  return {
    items,
    unreadMessageCount: data.unreadMessageCount ?? 0,
  };
}
